export type FeatureToggleNames =
  | 'planFlow'
  | 'modularReduxEnabled'
  | 'roleSelectCTA'
  | 'uspsValidationUnblock'
  | 'challengeRequested3dsEnabled'
  | 'preventNonUSTimezones'
  | 'improvedDownloadScreen'
  | 'personasServiceEnabled'
  | 'acquisitionRoleBasedRedirect'
  | 'growthRegistrationFlowFields'
  | 'acquisitionSetPlanMaybeLater'
  | 'parentFundWalletDesign'
  | 'defaultMax'
  | 'defaultInfinity'
  | 'ltaResetPasswordExperience'
  | 'kycRetryEnabled'
  | 'reOrderDebitOnFundingScreen'
  | 'onboardingNewUserOnboarding'
  | 'corePlanCopy'
  | 'kidFirstRegParentInviteV0'
  | 'linkToExternalBrowserWithBadWriaUrl'
  | 'onboardingWorkflow'
  | 'kidFirstRegParentInviteV1'
  | 'enableCorePriceChange'
  | 'riskFreePaintedDoor'
  | 'onboardingForwardAllLegacyRegistrationTrafficToLta'
  | 'enableGSRChange';

export interface FeatureToggleVariables {
  planFlow: {
    planName: string;
  };
  modularReduxEnabled: undefined | null | boolean;
  roleSelectCTA: boolean;
  uspsValidationUnblock: boolean;
  childsAccountHeader: boolean;
  challengeRequested3dsEnabled: undefined;
  preventNonUSTimezones: boolean;
  improvedDownloadScreen: boolean;
  personasServiceEnabled: boolean;
  acquisitionRoleBasedRedirect: boolean;
  growthRegistrationFlowFields: boolean;
  acquisitionSetPlanMaybeLater: boolean;
  parentFundWalletDesign: boolean;
  defaultMax: boolean;
  defaultInfinity: boolean;
  ltaResetPasswordExperience: boolean;
  kycRetryEnabled: boolean;
  reOrderDebitOnFundingScreen: boolean;
  onboardingNewUserOnboarding: boolean;
  corePlanCopy: boolean;
  kidFirstRegParentInviteV0: boolean;
  linkToExternalBrowserWithBadWriaUrl: boolean;
  onboardingWorkflow: boolean;
  kidFirstRegParentInviteV1: {
    variant?: 'email' | 'sms';
  };
  enableCorePriceChange: boolean;
  enableGSRChange: boolean;
  riskFreePaintedDoor: boolean;
  onboardingForwardAllLegacyRegistrationTrafficToLta: boolean;
}

export interface FeatureToggleStatus<T = Record<string, unknown>> {
  enabled: boolean;
  variationKey?: string;
  variables?: T;
}

export type FeatureTogglesConfig = {
  [FeatureToggleName in FeatureToggleNames]: {
    name: FeatureToggleName;
    optimizelyName: string;
    unleashName?: string;
    segmentPropertyName?: string;
    default: FeatureToggleStatus<FeatureToggleVariables[FeatureToggleName]>;
  };
};

export const FEATURE_TOGGLES: FeatureTogglesConfig = {
  planFlow: {
    name: 'planFlow',
    optimizelyName: 'acquisitionfire_planFlow',
    segmentPropertyName: 'planFlowVariation',
    default: {
      enabled: false
    }
  },
  modularReduxEnabled: {
    name: 'modularReduxEnabled',
    optimizelyName: 'acquisitionfire_modularReduxEnabled',
    default: {
      enabled: true
    }
  },
  roleSelectCTA: {
    name: 'roleSelectCTA',
    optimizelyName: 'acquisitionfire_roleSelectCTA',
    default: {
      enabled: false
    }
  },
  uspsValidationUnblock: {
    name: 'uspsValidationUnblock',
    optimizelyName: 'aquisition_uspsValidationUnblock',
    default: {
      enabled: true
    }
  },
  challengeRequested3dsEnabled: {
    name: 'challengeRequested3dsEnabled',
    optimizelyName: 'acquisitionfire_challengeRequested3ds',
    default: {
      enabled: false
    }
  },
  preventNonUSTimezones: {
    name: 'preventNonUSTimezones',
    optimizelyName: 'acquisitionfire_preventNonUSTimezones',
    default: {
      enabled: false
    }
  },
  improvedDownloadScreen: {
    name: 'improvedDownloadScreen',
    optimizelyName: 'acquisition_improvedDownloadScreen',
    default: {
      enabled: false
    }
  },
  personasServiceEnabled: {
    name: 'personasServiceEnabled',
    optimizelyName: 'personalization_shouldUsePersonas',
    default: {
      enabled: false
    }
  },
  growthRegistrationFlowFields: {
    name: 'growthRegistrationFlowFields',
    optimizelyName: 'growth_registration_flow_fields',
    default: {
      enabled: false
    }
  },
  acquisitionSetPlanMaybeLater: {
    name: 'acquisitionSetPlanMaybeLater',
    optimizelyName: 'acquisition-set_plan_maybe_later',
    default: {
      enabled: false
    }
  },
  parentFundWalletDesign: {
    name: 'parentFundWalletDesign',
    optimizelyName: 'acquisition_parentFundWalletDesign',
    default: {
      enabled: false
    }
  },
  acquisitionRoleBasedRedirect: {
    name: 'acquisitionRoleBasedRedirect',
    optimizelyName: 'acquisition_roleBasedRedirect',
    default: {
      enabled: false
    }
  },
  defaultMax: {
    name: 'defaultMax',
    optimizelyName: 'acquisition_defaultMax',
    default: {
      enabled: false
    }
  },
  defaultInfinity: {
    name: 'defaultInfinity',
    optimizelyName: 'acquisition_defaultInfinity',
    default: {
      enabled: false
    }
  },
  ltaResetPasswordExperience: {
    name: 'ltaResetPasswordExperience',
    optimizelyName: 'acquisition_ltaResetPassword',
    default: {
      enabled: false
    }
  },
  kycRetryEnabled: {
    name: 'kycRetryEnabled',
    optimizelyName: 'acquisition_kycHardDeclineRetry',
    default: {
      enabled: false
    }
  },
  reOrderDebitOnFundingScreen: {
    name: 'reOrderDebitOnFundingScreen',
    optimizelyName: 'acquisition_reOrderDebitOnFundingScreen',
    default: {
      enabled: false
    }
  },
  onboardingNewUserOnboarding: {
    name: 'onboardingNewUserOnboarding',
    optimizelyName: 'onboarding_newUserOnboarding',
    default: {
      enabled: false
    }
  },
  corePlanCopy: {
    name: 'corePlanCopy',
    optimizelyName: 'acquisition_corePlanCopy',
    default: {
      enabled: false
    }
  },
  kidFirstRegParentInviteV0: {
    name: 'kidFirstRegParentInviteV0',
    optimizelyName: 'acquisitionfire_kidFirstRegParentInviteV0',
    default: {
      enabled: false
    }
  },
  linkToExternalBrowserWithBadWriaUrl: {
    name: 'linkToExternalBrowserWithBadWriaUrl',
    optimizelyName: 'acquisition_linkToExternalBrowserWithBadWriaUrl',
    default: {
      enabled: false
    }
  },
  onboardingWorkflow: {
    name: 'onboardingWorkflow',
    optimizelyName: 'onboarding_workflow',
    default: {
      enabled: false
    }
  },
  kidFirstRegParentInviteV1: {
    name: 'kidFirstRegParentInviteV1',
    optimizelyName: 'acquisitionfire_kidFirstRegParentInviteV1',
    default: {
      enabled: false
    }
  },
  enableCorePriceChange: {
    name: 'enableCorePriceChange',
    optimizelyName: '',
    unleashName: 'acquisition.ACQ-6171.enable-core-price-change',
    default: {
      enabled: false
    }
  },
  enableGSRChange: {
    name: 'enableGSRChange',
    optimizelyName: '',
    unleashName: 'acquisition.ACQ-6171.enable-gsr-change',
    default: {
      enabled: false
    }
  },
  riskFreePaintedDoor: {
    name: 'riskFreePaintedDoor',
    optimizelyName: 'onboarding_riskFreePaintedDoor',
    default: {
      enabled: false
    }
  },
  onboardingForwardAllLegacyRegistrationTrafficToLta: {
    name: 'onboardingForwardAllLegacyRegistrationTrafficToLta',
    optimizelyName: 'onboarding_forward_all_legacy_registration_traffic_to_lta',
    default: {
      enabled: true
    }
  }
};
